// Le script à intégrer dans le produit : ses domaines autorisés, et la ligne à
// copier. Une réponse rejoint le baromètre ouvert du projet, sans identité ; sans
// vague ouverte, le script n'affiche rien.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Eyebrow, Note } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';

interface WidgetConfig {
  publicKey: string | null;
  origins: string[];
}

export function WidgetSection({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const url = `/api/projects/${encodeURIComponent(projectId)}/widget`;
  const queryKey = ['widget', projectId] as const;
  const config = useQuery({ queryKey, queryFn: () => api.get<WidgetConfig>(url) });
  const save = useMutation({
    mutationFn: (origins: string[]) => api.put<WidgetConfig>(url, { origins }),
    onSuccess: () => { setDraft(null); return qc.invalidateQueries({ queryKey }); },
  });
  const canWrite = useCanWrite(projectId);
  const [draft, setDraft] = useState<string | null>(null);

  if (config.isPending || config.isError) return null;
  const { publicKey, origins } = config.data;
  const text = draft ?? origins.join('\n');
  const snippet = publicKey
    ? `<script src="${window.location.origin}/widget.js" data-projet="${publicKey}" async></script>`
    : null;

  return (
    <section className="zy-card opt-section" aria-labelledby="widget-title">
      <div className="opt-head">
        <div>
          <Eyebrow>Script</Eyebrow>
          <h2 className="t-h3" id="widget-title">Recueillir des retours dans votre produit</h2>
        </div>
      </div>
      <p className="zy-texte-muet">
        Une ligne à coller dans votre produit : vos utilisateurs donnent une note et un commentaire sans
        quitter leur écran. Les réponses rejoignent le baromètre en cours, sans nom ni cookie. Il revient à
        l’éditeur du produit d’en informer ses utilisateurs.
      </p>

      {canWrite ? (
        <>
          <label className="zy-field">
            <span className="zy-field__label">Domaines autorisés, un par ligne</span>
            <textarea className="zy-input" rows={3} value={text} placeholder="https://app.monproduit.fr"
              onChange={(e) => setDraft(e.target.value)} />
          </label>
          {save.error ? <ErrorBanner>{save.error instanceof ApiRequestError ? save.error.message : 'L’enregistrement a échoué.'}</ErrorBanner> : null}
          <Button variant="ghost" loading={save.isPending} disabled={!text.trim()}
            onClick={() => save.mutate(text.split('\n').map((s) => s.trim()).filter(Boolean))}>
            Enregistrer les domaines
          </Button>
        </>
      ) : null}

      {snippet ? (
        <Note tone="succes">
          <p>À coller avant la balise de fin de page :</p>
          <code>{snippet}</code>
          {origins.length === 0 ? <p>Aucun domaine valide : le script ne s’affichera nulle part.</p> : null}
        </Note>
      ) : null}
    </section>
  );
}
